import BaseAPI, { querySuffix } from "./BaseAPI";
import { productRoute } from "../routes";

export default class ProductAPI extends BaseAPI {
    constructor(emitterInstance) {
        super(emitterInstance, productRoute, "product");
    }

    async ListWithIntakeID(intakeID) {
        return this._request("get", this._url(`?intake_id=${intakeID}`));
    }

    async ListAggregatedByStock(params = {}) {
        return this._request("get", this._url(querySuffix({ aggregate: "by-stock", ...params })));
    }

    /**
     * Server-assembled view for printing a label: returns product + client +
     * producer (user) + each ingredient with its stock, intake (with full
     * traceability fields), and supplier inlined. Replaces the per-render
     * dance the print component does today (Meat.GetByID + supplier lookup +
     * stock cross-ref + figureOutMeatID/figureOutSupplierID).
     */
    async GetLabelContext(id) {
        return this._request("get", this._url(`/${id}/label-context`));
    }

    /**
     * Recursive trace view for the Product Trace report. Returns the product
     * plus its fully-flattened raw supplier lots (the multi-stage chain expanded
     * down to flour/margarine/chicken/etc.) and the nested consumption tree
     * linking each in-house stage to its production run. For a basic single-
     * stage product `flattened` equals the direct ingredients and the tree is
     * one level deep.
     *
     * @param {string} id - Product ID
     * @returns {Promise<{product, client, location, producer, flattened, tree}>}
     */
    async GetTrace(id) {
        return this._request("get", this._url(`/${id}/trace`));
    }

    /**
     * Renders a label for the given product using a stored HTML template.
     * The backend assembles the label context, runs the template through
     * html/template, and converts the HTML to PDF via Gotenberg. Returns the
     * PDF as a Blob suitable for object URLs and printing.
     *
     * @param {string} id - Product ID
     * @param {Object} opts
     * @param {string} opts.template_id - LabelTemplate ObjectID hex
     * @param {boolean} [opts.box] - Whether to render the box label variant
     * @param {number} [opts.products_per_box] - Optional one-off box quantity
     * @returns {Promise<Blob>}
     */
    async RenderLabel(id, { template_id, box = false, products_per_box = 0 } = {}) {
        const params = new URLSearchParams({ template_id });
        if (box) params.set("box", "true");
        if (Number(products_per_box) > 0) {
            params.set("products_per_box", String(Math.trunc(Number(products_per_box))));
        }
        return this._request("get", this._url(`/${id}/labels.pdf?${params.toString()}`), null, {
            responseType: "blob",
            rawData: true,
            timeout: 0 // server-side Gotenberg PDF render — no global timeout
        });
    }

    /**
     * Renders one multi-page PDF containing a label per product. All products
     * share the supplied template and box flag. Returns the PDF as a Blob.
     *
     * @param {string[]} productIds - Array of product ObjectID hex strings
     * @param {Object} opts
     * @param {string} opts.template_id - LabelTemplate ObjectID hex
     * @param {boolean} [opts.box] - Whether to render the box-label variant
     * @param {number} [opts.products_per_box] - Optional one-off quantity for every product
     * @returns {Promise<Blob>}
     */
    async RenderLabelsBulk(
        productIds,
        { template_id, box = false, products_per_box = 0 } = {}
    ) {
        return this._request(
            "post",
            this._url(`/labels.pdf`),
            {
                product_ids: productIds,
                template_id,
                box,
                ...(Number(products_per_box) > 0
                    ? { products_per_box: Math.trunc(Number(products_per_box)) }
                    : {})
            },
            // server-side Gotenberg multi-page PDF render — no global timeout
            { responseType: "blob", rawData: true, timeout: 0 }
        );
    }

    async MarkOutOfStock(id) {
        const result = await this._request("put", this._url(`/${id}/out-of-stock`));
        this._emit("mark-out-of-stock", id);
        return result;
    }

    async MarkArchived(id) {
        const result = await this._request("put", this._url(`/${id}/archive`));
        this._emit("mark-archived", id);
        return result;
    }

    async UnmarkArchived(id) {
        const result = await this._request("delete", this._url(`/${id}/archive`));
        this._emit("unmark-archived", id);
        return result;
    }

    async MarkRecalled(id) {
        const result = await this._request("put", this._url(`/${id}/recall`));
        this._emit("mark-recalled", id);
        return result;
    }
}
